import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { tenantRoadmapsCRUDService } from '@/services/tenantRoadmapsCRUD';
import { tenantRoadmapsEntity } from '@/config/entities/tenantRoadmaps';
import { ArchiveBoxIcon, DocumentDuplicateIcon, PlayIcon, PauseIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { RoadmapCard, ViewSwitcher, CardGrid, ViewMode, FilterToolbar } from '@/components/cards';
import { useCardViewRefresh } from '@/hooks/useCardViewRefresh';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

/**
 * Tenant Roadmaps Page
 * 
 * Displays a CRUD table of downloaded roadmaps that the tenant can manage.
 * Users can view, edit, archive, and delete their downloaded roadmaps.
 */
export const TenantRoadmapsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // View mode state - default to cards
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('roadmaps-view-mode');
    return (saved === 'cards' || saved === 'table') ? saved : 'cards';
  });

  // Card view data state
  const [cardData, setCardData] = useState<any[]>([]);
  const [filteredCardData, setFilteredCardData] = useState<any[]>([]);
  const [cardLoading, setCardLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>('relevance');
  
  // Use generic card view refresh hook
  const { refreshKey, refresh } = useCardViewRefresh(viewMode);

  // Delete confirmation dialog state (shared for card and table views)
  const [roadmapToDelete, setRoadmapToDelete] = useState<any | null>(null);
  const [skipDeleteConfirmation, setSkipDeleteConfirmation] = useState(false);

  // Save view mode preference
  useEffect(() => {
    localStorage.setItem('roadmaps-view-mode', viewMode);
  }, [viewMode]);

  // Load card data when in card view
  useEffect(() => {
    if (viewMode === 'cards') {
      loadCardData();
    }
  }, [viewMode, refreshKey]);

  const loadCardData = async () => {
    try {
      setCardLoading(true);
      const response = await tenantRoadmapsCRUDService.getAll({});
      const roadmaps = response || [];
      setCardData(roadmaps);
      applyFiltersAndSort(roadmaps, searchTerm, activeFilters, sortBy);
    } catch (error) {
      console.error('Error loading roadmaps for card view:', error);
    } finally {
      setCardLoading(false);
    }
  };

  // Apply filters and sorting to card data
  const applyFiltersAndSort = (
    data: any[],
    search: string,
    filters: string[],
    sort: string
  ) => {
    let filtered = [...data];

    // Apply search
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.functional_area?.toLowerCase().includes(searchLower) ||
        item.industry?.toLowerCase().includes(searchLower)
      );
    }

    // Apply filters
    if (filters.length > 0) {
      filtered = filtered.filter(item => {
        if (filters.includes('active') && item.status === 'ACTIVE') return true;
        if (filters.includes('draft') && item.status === 'DRAFT') return true;
        if (filters.includes('archived') && item.status === 'ARCHIVED') return true;
        if (filters.includes('completed') && item.status === 'COMPLETED') return true;
        if (filters.includes('inactive') && item.status === 'INACTIVE') return true;
        return false;
      });
    }

    // Apply sorting
    switch (sort) {
      case 'name':
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        break;
      case 'date-newest':
        filtered.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
        break;
      case 'date-oldest':
        filtered.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
        break;
      case 'relevance':
      default:
        // Smart defaults: active first, then by date
        filtered.sort((a, b) => {
          const aActive = a.status === 'ACTIVE';
          const bActive = b.status === 'ACTIVE';
          if (aActive && !bActive) return -1;
          if (!aActive && bActive) return 1;
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        });
        break;
    }

    setFilteredCardData(filtered);
  };

  // Update filtered data when search, filters, or sort changes
  useEffect(() => {
    if (viewMode === 'cards' && cardData.length > 0) {
      applyFiltersAndSort(cardData, searchTerm, activeFilters, sortBy);
    }
  }, [searchTerm, activeFilters, sortBy, cardData, viewMode]);

  const handleViewRoadmap = (item: any) => {
    // Primary roadmap click path: open tenant roadmap detail page first.
    navigate(`/tenant/roadmaps/${item.id}`);
  };

  const handleEditRoadmap = (item: any) => {
    // Explicit edit path: open roadmap builder/editor.
    navigate(`/tenant/roadmaps/builder?roadmap=${item.id}`);
  };

  const handleDeleteRoadmap = async (item: any) => {
    try {
      await tenantRoadmapsCRUDService.delete(item.id);
      refresh(); // Use generic refresh hook
    } catch (error) {
      console.error('Error deleting roadmap:', error);
    }
  };

  const handleDeleteRoadmapWithConfirm = (item: any) => {
    if (skipDeleteConfirmation) {
      handleDeleteRoadmap(item);
    } else {
      setRoadmapToDelete(item);
    }
  };

  const handleConfirmDeleteRoadmap = async () => {
    if (!roadmapToDelete) return;
    await handleDeleteRoadmap(roadmapToDelete);
  };

  const handleArchiveRoadmap = async (item: any) => {
    try {
      await tenantRoadmapsCRUDService.archiveRoadmap(item.id);
      refresh(); // Use generic refresh hook
    } catch (error) {
      console.error('Error archiving roadmap:', error);
    }
  };

  const handleToggleStatus = async (item: any) => {
    try {
      const newStatus = item.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await tenantRoadmapsCRUDService.toggleStatus(item.id, newStatus === 'ACTIVE');
      refresh(); // Use generic refresh hook
    } catch (error) {
      console.error('Error toggling roadmap status:', error);
    }
  };

  const handleCopyRoadmap = async (item: any) => {
    try {
      // Create a copy of the roadmap with a new name
      const copyData = {
        ...item,
        name: `${item.name} (Copy)`,
        status: 'ACTIVE'
      };
      delete copyData.id; // Remove the ID so a new one is generated
      delete copyData.created_at;
      delete copyData.updated_at;
      
      await tenantRoadmapsCRUDService.create(copyData);
      refresh(); // Use generic refresh hook
    } catch (error) {
      console.error('Error copying roadmap:', error);
    }
  };

  const handleCreate = () => {
    // Just navigate to builder - don't create roadmap until user clicks Save
    // This matches template behavior: template is only created when user clicks Save
    navigate('/tenant/roadmaps/builder');
  };

  return (
    <div>
        <div className="mb-4 flex justify-between items-center">
          {viewMode === 'cards' && (
            <FilterToolbar
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
              searchPlaceholder={t('tenantRoadmaps.searchRoadmapsPlaceholder') || 'Search roadmaps...'}
              quickFilters={[
                { id: 'active', label: 'Active', value: 'active' },
                { id: 'draft', label: 'Draft', value: 'draft' },
                { id: 'archived', label: 'Archived', value: 'archived' },
                { id: 'completed', label: 'Completed', value: 'completed' },
                { id: 'inactive', label: 'Inactive', value: 'inactive' }
              ]}
              activeFilters={activeFilters}
              onFilterToggle={(filterId) => {
                setActiveFilters(prev =>
                  prev.includes(filterId)
                    ? prev.filter(id => id !== filterId)
                    : [...prev, filterId]
                );
              }}
              sortOptions={[
                { id: 'relevance', label: 'Relevance', value: 'relevance' },
                { id: 'name', label: 'Name (A-Z)', value: 'name' },
                { id: 'date-newest', label: 'Date (Newest)', value: 'date-newest' },
                { id: 'date-oldest', label: 'Date (Oldest)', value: 'date-oldest' }
              ]}
              sortValue={sortBy}
              onSortChange={setSortBy}
            />
          )}
          <ViewSwitcher
            currentView={viewMode}
            onViewChange={setViewMode}
          />
        </div>

        {viewMode === 'table' ? (
          <div>
            <FlexibleCRUDTable
              key={refreshKey}
              entity={tenantRoadmapsEntity}
              service={tenantRoadmapsCRUDService}
              onView={handleEditRoadmap}
              viewActionIcon={PencilIcon}
              viewActionLabel={t('common.edit') || 'Edit'}
              onCreate={handleCreate}
              onDelete={handleDeleteRoadmap}
              useConfirmDialog={true}
              skipDeleteConfirmation={skipDeleteConfirmation}
              onDeleteDontAskAgain={() => setSkipDeleteConfirmation(true)}
              showCreateButton={false}
              searchPlaceholder={t('tenantRoadmaps.searchRoadmapsPlaceholder')}
              customActions={[
                {
                  label: (item: any) => item.status === 'ACTIVE' ? 'Deactivate' : 'Activate',
                  icon: (item: any) => item.status === 'ACTIVE' ? PauseIcon : PlayIcon,
                  onClick: handleToggleStatus,
                  variant: (item: any) => item.status === 'ACTIVE' ? 'secondary' : 'primary'
                },
                {
                  label: 'Archive',
                  icon: ArchiveBoxIcon,
                  onClick: handleArchiveRoadmap,
                  variant: 'secondary'
                },
                {
                  label: 'Copy',
                  icon: DocumentDuplicateIcon,
                  onClick: handleCopyRoadmap,
                  variant: 'secondary'
                }
              ]}
              onOperationComplete={() => {
                refresh(); // Use generic refresh hook
              }}
            />
          </div>
        ) : (
          <div>
            {cardLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-text)] mx-auto"></div>
                <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">Loading...</p>
              </div>
            ) : filteredCardData.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-[var(--color-secondary-text)] opacity-75">
                  {cardData.length === 0
                    ? 'No roadmaps found'
                    : 'No roadmaps match your filters'}
                </p>
              </div>
            ) : (
              <CardGrid columns={4}>
                {filteredCardData.map((roadmap) => (
                  <RoadmapCard
                    key={roadmap.id}
                    roadmap={roadmap}
                    onView={handleViewRoadmap}
                    onEdit={handleEditRoadmap}
                    onDelete={handleDeleteRoadmapWithConfirm}
                    onCopy={handleCopyRoadmap}
                    customActions={[
                      {
                        label: (item: any) => item.status === 'ACTIVE' ? 'Deactivate' : 'Activate',
                        icon: (item: any) => item.status === 'ACTIVE' ? PauseIcon : PlayIcon,
                        onClick: handleToggleStatus,
                        variant: (item: any) => item.status === 'ACTIVE' ? 'secondary' : 'primary'
                      },
                      {
                        label: 'Archive',
                        icon: ArchiveBoxIcon,
                        onClick: handleArchiveRoadmap,
                        variant: 'secondary'
                      }
                    ]}
                  />
                ))}
              </CardGrid>
            )}
          </div>
        )}

        <ConfirmDialog
          isOpen={!!roadmapToDelete}
          onClose={() => setRoadmapToDelete(null)}
          onConfirm={handleConfirmDeleteRoadmap}
          title={
            roadmapToDelete
              ? t('messages.confirmDeletePrompt') || 'Are you sure you want to delete?'
              : ''
          }
          itemName={roadmapToDelete?.name}
          description={t('messages.deleteWarning') || 'This action cannot be undone.'}
          confirmLabel={t('common.delete') || 'Delete'}
          cancelLabel={t('common.cancel') || 'Cancel'}
          variant="danger"
          showDontAskAgain={true}
          dontAskAgainLabel={t('common.dontAskAgain') || "Don't ask again for this session"}
          onDontAskAgainChange={(checked) => checked && setSkipDeleteConfirmation(true)}
        />
    </div>
  );
};
